import { useMutation } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { inspectionService } from '@/services/inspection.service';
import { Button } from '@/components/ui/button';
import { toast, swalConfirm } from '@/lib/swal';
import { getApiErrorMessage } from '@/lib/api-error';

interface ApproveInspectionButtonProps {
  inspectionId: string;
  inspectionNumber: string;
  onApproved: () => void;
  size?: 'sm' | 'default';
}

/** Reused approve action — same operationsApprove() call + confirm dialog
 * already used inline in InspectionDetailPage.tsx and OperationsReviewsTable.tsx,
 * extracted here so the Review Workspace doesn't add a fourth copy. */
export function ApproveInspectionButton({
  inspectionId,
  inspectionNumber,
  onApproved,
  size = 'default',
}: ApproveInspectionButtonProps) {
  const approveMutation = useMutation({
    mutationFn: () => inspectionService.operationsApprove(inspectionId),
    onSuccess: () => {
      toast.success('Approved for commercial');
      onApproved();
    },
    onError: (e: Error) => toast.error(getApiErrorMessage(e, 'Failed to approve')),
  });

  const handleApprove = async () => {
    const ok = await swalConfirm({
      title: 'Approve for Commercial?',
      text: `This will mark ${inspectionNumber} as approved and move it to commercial review.`,
      confirmText: 'Approve',
    });
    if (ok) approveMutation.mutate();
  };

  return (
    <Button size={size} onClick={() => void handleApprove()} disabled={approveMutation.isPending}>
      {approveMutation.isPending && (
        <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
      )}
      Approve
    </Button>
  );
}
